import { formatCurrency, formatDate } from "@angular/common";
import { ValidationErrors } from "@angular/forms";

type ErrorEntry = string | ((value: unknown) => string);

function formatFutureDateMessage(value: unknown): string {
  if (!value || typeof value !== "object") {
    return "La fecha seleccionada no puede ser posterior al día permitido.";
  }
  const v = value as {
    selectedDate?: Date | string;
    maxDate?: Date | string;
  };
  const selected = v.selectedDate != null ? new Date(v.selectedDate) : null;
  const max = v.maxDate != null ? new Date(v.maxDate) : null;
  if (!selected || !max || Number.isNaN(selected.getTime()) || Number.isNaN(max.getTime())) {
    return "La fecha seleccionada no puede ser posterior al día permitido.";
  }
  return `La fecha seleccionada ${formatDate(selected, "dd/MM/yyyy", "es-CO")} no puede ser posterior a ${formatDate(max, "dd/MM/yyyy", "es-CO")}.`;
}

export const APP_FORM_ERRORS = (): Record<string, ErrorEntry> => {
  return {
    required: "Este campo es requerido",
    maxlength: (value: unknown) => {
      const { requiredLength, actualLength } = value as ValidationErrors;
      return `Longitud máxima permitida ${requiredLength}, longitud actual ${actualLength}`;
    },
    email: "No cumple con el formato de correo válido",
    minlength: (value: unknown) => {
      const { requiredLength, actualLength } = value as ValidationErrors;
      return `Longitud mínima permitida ${requiredLength}, longitud actual ${actualLength}`;
    },
    passwordMismatch: "Las contraseñas no coinciden.",
    max: (value: unknown) => {
      const { max, actual } = value as ValidationErrors;
      return `El valor máximo permitido es ${max}, Valor actual ${actual}`;
    },
    otp_error_length: "El código ingresado está incompleto",
    underAge: (value: unknown) => {
      if (typeof value === "object" && value !== null && "actual" in value && "required" in value) {
        const v = value as { actual: number; required: number };
        return `La persona no cumple la edad mínima requerida (${v.required} años). Edad actual: ${v.actual}.`;
      }
      return "La persona debe ser mayor de edad";
    },
    overAge: (value: unknown) => {
      const v = value as { actual: number; maximum: number };
      return `La edad máxima permitida es ${v.maximum} años. Edad actual: ${v.actual}.`;
    },
    min: (value: unknown) => {
      const { actual, min } = value as ValidationErrors;
      return `El valor mínimo permitido es ${min}, valor actual ${actual}`;
    },
    pattern: "No cumple con el formato requerido",
    invalidPasswordFormat:
      "No cumple con el formato requerido: al menos una letra mayúscula, al menos una letra minúscula, al menos un dígito, sin espacios en blanco, mínimo 7 caracteres y máximo 10.",
    requiredNumberLength: (value: unknown) => {
      const { required, actual } = value as ValidationErrors;
      return `Longitud mínima permitida ${required}, longitud actual ${actual}`;
    },
    max_files: (value: unknown) => {
      const { max_files, current_files } = value as ValidationErrors;
      return `La cantidad maxima de archivos permitidos es ${max_files}, cantidad actual ${current_files}`;
    },
    max_file_size: (value: unknown) => {
      const { max_size } = value as ValidationErrors;
      const limit = max_size ?? 3;
      return `La imagen no puede pesar más de ${limit} MB.`;
    },
    file_type: (value: unknown) => {
      const { accepted_types, current_type, file } = value as ValidationErrors;
      return `El archivo ${file} es de tipo ${current_type}, los tipos permitidos son ${accepted_types}`;
    },
    maxDate: (value: unknown) => {
      const { max, selected } = value as ValidationErrors;
      return `La fecha seleccionada ${formatDate(selected, "yyyy-MM-dd", "es-CO")} es mayor a la fecha máxima permitida ${formatDate(max, "yyyy-MM-dd", "es-CO")}`;
    },
    minDate: (value: unknown) => {
      const { min, selected } = value as ValidationErrors;
      return `La fecha seleccionada ${formatDate(selected, "yyyy-MM-dd", "es-CO")} es menor a la fecha mínima permitida ${formatDate(min, "yyyy-MM-dd", "es-CO")}`;
    },
    max_options_allowed: (value: unknown) => {
      const { max_options_allowed, current_options } = value as ValidationErrors;
      return `La cantidad máxima de usuarios permitidos son ${max_options_allowed}, cantidad actual ${current_options}`;
    },
    min_options_allowed: (value: unknown) => {
      const { min_options_allowed, current_options } = value as ValidationErrors;
      return `La cantidad mínima de usuarios permitidos son ${min_options_allowed}, cantidad actual ${current_options}`;
    },
    dateRange: (value: unknown) =>
      typeof value === "string"
        ? value
        : String((value as ValidationErrors)?.["dateRange"] ?? ""),
    futureDate: (value: unknown) => formatFutureDateMessage(value),
    invalidDate: "La fecha ingresada no es válida",
    olderThanAllowed: (value: unknown) => {
      const v = value as { daysAllowed?: number };
      const days = v.daysAllowed ?? 0;
      return `La fecha no puede ser anterior al rango permitido (${days} día(s) hacia atrás desde hoy).`;
    },
    invalidRange: (value: unknown) => {
      const v = value as { start?: number; end?: number };
      if (v.start !== undefined && v.end !== undefined) {
        return `El valor final (${v.end}) debe ser mayor al inicial (${v.start})`;
      }
      return "El rango de valores no es válido.";
    },
    invalidNumbers: "Los valores deben ser números válidos.",
    minValue: (value: unknown) => {
      const { actual, min } = value as ValidationErrors;
      return `El valor mínimo permitido es ${min}, valor actual ${actual}`;
    },
    maxValue: (value: unknown) => {
      const max = Number((value as { max?: number })?.max);
      if (!Number.isFinite(max)) {
        return "El valor supera el monto permitido.";
      }
      return `El valor no puede superar ${formatCurrency(max, "es-CO", "$", "COP")}.`;
    },
    notANumber: "Ingrese un valor numérico válido",
    emailsMismatch: "Los correos no coinciden.",
    whitespaceOnly: "El texto no puede estar compuesto solo por espacios en blanco",
    invalidMedium: "Seleccione un medio de comunicación válido",
    invalidDictionaryValue: "Seleccione una opción válida",
    leadingOrTrailingSpaces:
      "El texto no puede comenzar ni terminar con espacios en blanco",
    arrayMinLength: (value: unknown) => {
      const v = value as { min?: number; actual?: number };
      return `Se requieren al menos ${v.min} elemento(s); hay ${v.actual ?? 0}.`;
    },
    arrayMaxLength: (value: unknown) => {
      const v = value as { max?: number; actual?: number };
      return `Se permiten como máximo ${v.max} elemento(s); hay ${v.actual ?? 0}.`;
    },
    invalidDates: "Las fechas ingresadas no son válidas.",
    invalidDateRange:
      "La fecha de inicio debe ser anterior o igual a la fecha final.",
    atLeastOneAmount: "Al menos uno de los valores debe ser mayor a cero.",
  };
};
